import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { FileText, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DocumentsCard = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/documents`);
      setDocuments(data.documents);
    } catch (e) {
      // silent — card just shows empty state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const view = (doc) => {
    const token = localStorage.getItem("token");
    window.open(`${API_URL}/api/documents/${doc.id}/download?auth=${token}`, "_blank", "noopener");
  };

  return (
    <Card className="border-stone-200" data-testid="documents-card">
      <CardHeader>
        <CardTitle style={{ fontFamily: "Manrope, sans-serif" }}>Documents</CardTitle>
        <CardDescription>Legal & compliance documents from CPC</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-stone-500 text-sm py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading documents…
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-8 text-stone-400 text-sm" data-testid="documents-empty">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No documents yet. CPC's agreements and authorization forms will appear here.
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between py-3 gap-3" data-testid={`document-row-${doc.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-emerald-700" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{doc.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="secondary" className="text-[10px] bg-stone-100 text-stone-600">{doc.category}</Badge>
                      <span className="text-xs text-stone-400">{formatSize(doc.size)}</span>
                    </div>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full flex-shrink-0" onClick={() => view(doc)} data-testid={`document-download-${doc.id}`}>
                  <Download className="w-3.5 h-3.5 mr-1.5" /> View
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
